/**
 * Super Admin Guard.
 *
 * Stricter sibling of `<AdminGuard>` — admits only `role === 'super_admin'`.
 * Use on routes whose sidebar entry is gated by `adminOnly: true` (which
 * `Sidebar.tsx` filters via `isSuperAdmin`), or on any cross-org admin
 * surface where regular org-level admins should not have visibility.
 *
 * Behavior:
 *  - Portal user      → their portal home (matches AdminGuard)
 *  - admin (non-super) or unknown → /dashboard (admin home; AdminGuard above
 *    has already let them in, so they remain authenticated)
 *  - super_admin      → renders children
 */

import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePortalHome } from '@/hooks/portal/usePortalHome';

interface SuperAdminGuardProps {
  children: ReactNode;
}

export function SuperAdminGuard({ children }: SuperAdminGuardProps) {
  const { isSuperAdmin, isPortalUser } = useAuth();
  const home = usePortalHome();

  if (isPortalUser) {
    return <Navigate to={home} replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
